import type { Cell, Direction } from '../sim/types';

export interface RenderPoint {
  readonly x: number;
  readonly y: number;
}

const FULL_TURN = Math.PI * 2;

/**
 * Converts the fixed-step movement state into a sub-tick render position.
 * Translation deliberately stays linear: easing every individual tile makes a
 * held run visibly brake and accelerate at every grid boundary.
 */
export function interpolateGridMovement(
  from: Cell,
  target: Cell,
  progress: number,
  moving: boolean,
  fixedStepAlpha: number,
  moveTicks: number,
): RenderPoint {
  const safeTicks = Number.isFinite(moveTicks) && moveTicks > 0 ? moveTicks : 1;
  const alpha = clamp01(fixedStepAlpha);
  const renderProgress = clamp01(progress + (moving ? alpha / safeTicks : 0));
  return {
    x: from.x + (target.x - from.x) * renderProgress,
    y: from.y + (target.y - from.y) * renderProgress,
  };
}

/**
 * Advances the run cycle from distance travelled instead of wall-clock time.
 * The pose therefore crosses cell seams without restarting and also remains
 * stable when a frame is late or the game is paused.
 */
export function advanceGaitPhase(
  phase: number,
  previous: RenderPoint,
  current: RenderPoint,
  moving: boolean,
  cyclesPerTile = 0.52,
): number {
  if (!moving || !isFinitePoint(previous) || !isFinitePoint(current)) return wrapAngle(phase);
  const distance = Math.hypot(current.x - previous.x, current.y - previous.y);
  const safeCycles = Number.isFinite(cyclesPerTile) ? Math.max(0, cyclesPerTile) : 0;
  return wrapAngle(phase + distance * FULL_TURN * safeCycles);
}

/**
 * Returns a normalized visual lead toward a buffered perpendicular turn. The
 * simulation and collision position stay strictly on-grid; only the character
 * pose leans into the corner during the final half of the current tile.
 */
export function cornerTurnLead(
  from: Cell,
  target: Cell,
  facing: Direction,
  progress: number,
  moving: boolean,
): RenderPoint {
  if (!moving) return { x: 0, y: 0 };
  const travelX = Math.sign(target.x - from.x);
  const travelY = Math.sign(target.y - from.y);
  if (travelX === 0 && travelY === 0) return { x: 0, y: 0 };

  const facingVector = directionVector(facing);
  // Same-direction travel needs no corner pose. Reversals retain their clear
  // skid/turn silhouette instead of visually cutting back across the tile.
  const alignment = travelX * facingVector.x + travelY * facingVector.y;
  if (alignment !== 0) return { x: 0, y: 0 };

  const anticipation = turnAnticipation(progress);
  return {
    x: facingVector.x * anticipation,
    y: facingVector.y * anticipation,
  };
}

/** Keeps the torso aligned to travel until a queued corner is close enough. */
export function presentationFacingAngle(
  from: Cell,
  target: Cell,
  facing: Direction,
  progress: number,
  moving: boolean,
): number {
  const wantedAngle = directionAngle(facing);
  if (!moving) return wantedAngle;
  const travelX = Math.sign(target.x - from.x);
  const travelY = Math.sign(target.y - from.y);
  if (travelX === 0 && travelY === 0) return wantedAngle;
  const facingVector = directionVector(facing);
  const alignment = travelX * facingVector.x + travelY * facingVector.y;
  if (alignment !== 0) return wantedAngle;

  const travelAngle = Math.atan2(travelY, travelX);
  return travelAngle + shortestAngleDelta(travelAngle, wantedAngle) * turnAnticipation(progress);
}

export function directionAngle(direction: Direction): number {
  if (direction === 'right') return 0;
  if (direction === 'down') return Math.PI / 2;
  if (direction === 'left') return Math.PI;
  return -Math.PI / 2;
}

/** Frame-rate-independent angular damping for character secondary motion. */
export function dampAngle(current: number, target: number, deltaMs: number, responseMs = 42): number {
  if (deltaMs <= 0) return current;
  if (!Number.isFinite(deltaMs)) return target;
  const safeResponse = Math.max(1, responseMs);
  const blend = 1 - Math.exp(-deltaMs / safeResponse);
  return current + shortestAngleDelta(current, target) * blend;
}

/** Frame-rate-independent scalar damping used to ease animation energy only. */
export function dampValue(current: number, target: number, deltaMs: number, responseMs: number): number {
  if (deltaMs <= 0) return current;
  if (!Number.isFinite(deltaMs)) return target;
  const safeResponse = Math.max(1, responseMs);
  const blend = 1 - Math.exp(-deltaMs / safeResponse);
  return current + (target - current) * blend;
}

export function shortestAngleDelta(from: number, to: number): number {
  let delta = (to - from) % FULL_TURN;
  if (delta > Math.PI) delta -= FULL_TURN;
  if (delta < -Math.PI) delta += FULL_TURN;
  return delta;
}

function directionVector(direction: Direction): RenderPoint {
  if (direction === 'right') return { x: 1, y: 0 };
  if (direction === 'down') return { x: 0, y: 1 };
  if (direction === 'left') return { x: -1, y: 0 };
  return { x: 0, y: -1 };
}

function isFinitePoint(point: RenderPoint): boolean {
  return Number.isFinite(point.x) && Number.isFinite(point.y);
}

function wrapAngle(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return ((value % FULL_TURN) + FULL_TURN) % FULL_TURN;
}

function smoothStep(value: number): number {
  return value * value * (3 - 2 * value);
}

function turnAnticipation(progress: number): number {
  return smoothStep(clamp01((progress - 0.46) / 0.54));
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}
